import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import Checkbox from 'ui/Checkbox';
import { groupBy } from 'lodash-es';
import { IBootstrapCountry } from 'services/BackendApi/types/country';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';
import { MultiSearchDropdownApi } from 'ui/MultiSearchDropdown/MultiSearchDropdownApi';
import { IMultiSearchDropdownOption } from 'ui/MultiSearchDropdown/MultiSearchDropdownOptions';
import { TAnnouncementRestrictType } from 'services/BackendApi/types/AnnouncementResponse';
import { makeBackendApi } from 'services/BackendApi/BackendApi';
import { ICompany } from 'services/BackendApi/types/CompaniesResponse';
import FluidButton from 'ui/FluidButton';

interface IApplicableToSectionProps {
  restrictType: TAnnouncementRestrictType;
  countryCodes: string[];
  travelCompanyUuids: string[];
  initialTravelCompanyOptions?: IMultiSearchDropdownOption[];
  validationErrors: {
    targeting?: boolean;
  };
  updateRestrictType: (type: TAnnouncementRestrictType) => void;
  updateCountryCodes: (codes: string[]) => void;
  resetCountryCodes: () => void;
  updateTravelCompanyUuids: (uuids: string[]) => void;
}

export const ApplicableToSection = ({
  restrictType,
  countryCodes,
  travelCompanyUuids,
  validationErrors,
  initialTravelCompanyOptions = [],
  updateRestrictType,
  updateCountryCodes,
  resetCountryCodes,
  updateTravelCompanyUuids,
}: IApplicableToSectionProps) => {
  const [openRegions, setOpenRegions] = useState<string[]>([]);

  const countries = useSelector(getBootstrapCountriesSelector);
  const countriesByRegion = useMemo(() => groupBy(countries, 'region'), [countries]);

  // Clear the appropriate state when switching between selection modes
  useEffect(() => {
    if (restrictType === 'BY_COUNTRIES' && travelCompanyUuids.length > 0) {
      // Switching to country mode - clear travel company selections
      updateTravelCompanyUuids([]);
    } else if (restrictType === 'BY_TRAVEL_COMPANIES' && countryCodes.length > 0) {
      // Switching to TC mode - clear country selections
      updateCountryCodes([]);
    }
  }, [restrictType]);

  const handleRegionToggle = (region: string, isOpen: boolean) => {
    setOpenRegions(prev => (isOpen ? [...prev, region] : prev.filter(r => r !== region)));
  };

  const handleCountryToggle = (countryCode: string, checked: boolean) => {
    if (checked) {
      updateCountryCodes([...countryCodes, countryCode]);
    } else {
      updateCountryCodes(countryCodes.filter(code => code !== countryCode));
    }
  };

  const getSelectedCountsForRegion = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedInRegion = regionCountries.filter(country => countryCodes.includes(country.code)).length;
    return selectedInRegion;
  };

  const getRegionSummaryText = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedCount = getSelectedCountsForRegion(region);

    // If no countries selected globally, show "All countries"
    if (countryCodes.length === 0) {
      return 'All countries';
    }

    // If all countries in this region are selected, show "All countries"
    if (selectedCount === regionCountries.length && regionCountries.length > 0) {
      return 'All countries';
    }

    // Otherwise show the count
    return `${selectedCount} countries`;
  };

  const handleRegionCheckboxToggle = (region: string, checked: boolean) => {
    const regionCountries = countriesByRegion[region] || [];
    const regionCodes = regionCountries.map(country => country.code);

    if (checked) {
      // Add all countries from this region
      const newCountryCodes = [...new Set([...countryCodes, ...regionCodes])];
      updateCountryCodes(newCountryCodes);
    } else {
      // Remove all countries from this region
      const newCountryCodes = countryCodes.filter(code => !regionCodes.includes(code));
      updateCountryCodes(newCountryCodes);
    }
  };

  const isRegionFullySelected = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    if (regionCountries.length === 0) return false;
    return regionCountries.every(country => countryCodes.includes(country.code));
  };

  const isRegionPartiallySelected = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedCount = getSelectedCountsForRegion(region);
    return selectedCount > 0 && selectedCount < regionCountries.length;
  };

  const isByCountryMode = restrictType === 'BY_COUNTRIES' || restrictType === 'ALL';
  const isByTcMode = restrictType === 'BY_TRAVEL_COMPANIES';

  const backendApi = makeBackendApi();

  return (
    <div className="flex flex-col gap-[20px]">
      {/* Section Title */}
      <span className="block font-bold text-[17px] leading-[22px] text-black font-pt-sans">Applicable to</span>

      {/* Radio Buttons Row */}
      <div className="flex items-center gap-[20px]">
        {/* Radio Option 1: Choose TC by Country */}
        <label className="flex items-center gap-[8px] cursor-pointer">
          <input
            type="radio"
            name="restrict-type"
            checked={isByCountryMode}
            onChange={() => updateRestrictType('BY_COUNTRIES')}
            className="w-[20px] h-[20px] border border-gray-40 appearance-none rounded-full checked:border-[6px] checked:border-green-prime bg-white-hard cursor-pointer"
          />
          <span className="text-[16px] leading-[21px] text-black font-pt-sans">Choose TC by country</span>
        </label>

        {/* Radio Option 2: Choose TC by Name */}
        <label className="flex items-center gap-[8px] cursor-pointer">
          <input
            type="radio"
            name="restrict-type"
            checked={isByTcMode}
            onChange={() => updateRestrictType('BY_TRAVEL_COMPANIES')}
            className="w-[20px] h-[20px] border border-gray-40 appearance-none rounded-full checked:border-[6px] checked:border-green-prime bg-white-hard cursor-pointer"
          />
          <span className="text-[16px] leading-[21px] text-black font-pt-sans">Choose TC by name</span>
        </label>
      </div>

      {/* Country Selection Mode */}
      {isByCountryMode && (
        <div className="flex flex-col gap-[10px]">
          {/* Header Row */}
          <div className="flex justify-between items-center">
            <span className="text-[15px] font-pt-sans text-gray-700">Show to TA from these Countries:</span>
            <div className="flex items-center gap-2.5">
              <span className="text-[15px] font-pt-sans text-brown-prime">
                {countryCodes.length === 0
                  ? 'All countries selected by default'
                  : countryCodes.length === 1
                  ? '1 country selected'
                  : `${countryCodes.length} countries selected`}
              </span>
              <FluidButton
                type="secondary"
                onClick={resetCountryCodes}
                className="bg-white border border-brown-prime text-brown-prime hover:bg-brown-prime hover:text-white"
              >
                Reset Selection
              </FluidButton>
            </div>
          </div>

          {/* Region Accordions */}
          <div className="flex flex-col gap-[0px]">
            {Object.entries(countriesByRegion).map(([region, regionCountries], index) => {
            const isOpen = openRegions.includes(region);
            const isEven = index % 2 === 0;

            return (
              <AccordionStackable
                key={region}
                isOpen={isOpen}
                onToggle={newIsOpen => handleRegionToggle(region, newIsOpen)}
                buttonClassName={`${isOpen ? '!bg-green-20' : isEven ? '!bg-ivory' : '!bg-white'}`}
                wrapperClassName="[&_.accordion-stackable-chevron_i]:text-brown-prime"
                summary={
                  <div className="flex justify-between items-center w-full mr-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isRegionFullySelected(region)}
                        indeterminate={isRegionPartiallySelected(region)}
                        onChange={e => handleRegionCheckboxToggle(region, (e.target as HTMLInputElement).checked)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="text-[15px] font-pt-sans text-black">{region}</span>
                    </div>
                    <span className="text-[13px] font-pt-sans text-gray-100">{getRegionSummaryText(region)}</span>
                  </div>
                }
              >
                <div className="p-5 grid grid-cols-5 gap-4">
                  {regionCountries.map(country => (
                    <label key={country.code} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={countryCodes.includes(country.code)}
                        onChange={e => handleCountryToggle(country.code, (e.target as HTMLInputElement).checked)}
                      />
                      <span className="text-[14px] font-pt-sans text-black">{country.name}</span>
                    </label>
                  ))}
                </div>
              </AccordionStackable>
            );
          })}
          </div>
        </div>
      )}

      {/* Travel Company Selection Mode */}
      {isByTcMode && (
        <div className="flex flex-col gap-[10px]" data-validation-id="targeting">
          <MultiSearchDropdownApi
            fieldId="tc-search"
            values={travelCompanyUuids}
            onValueChange={updateTravelCompanyUuids}
            initialSelectedOptions={initialTravelCompanyOptions}
            searchFn={async (searchTerm: string) => {
              const response = await backendApi.getCompanies({
                'filter[company][name:ilike]': searchTerm || undefined,
                'page[limit]': 50,
              });
              return response.data.data.map((company: ICompany) => ({
                value: company.uuid,
                label: company.name,
              }));
            }}
            minSearchLength={3}
            debounceMs={1000}
            withTags={true}
            inputClassName="w-full placeholder:italic"
            className="w-full"
            // label="Search Travel Companies"
            inputPlaceholder="Search travel companies..."
            maxVisibleItems={10}
          />
        </div>
      )}

      {/* ========================================
          BUSINESS REQUIREMENT (2025-11-18 - REVISED SAME DAY):
          - Travel Companies are NOT mandatory
          - If user selects "Choose TC by name" but leaves TC list empty,
            the system silently switches to "Choose TC by country" mode
            with empty country list (meaning "all countries") when saving
          - This auto-switch happens in cleanPayloadForApi function
          - Countries are NOT mandatory (empty = all countries)
          ========================================
      */}
    </div>
  );
};
